import type { NeedleAddress } from "../address.js";
import type { ProjectDocumentOver, ProjectDocumentTarget } from "./types.js";

export interface PropertyOverInput {
    readonly target: ProjectDocumentTarget;
    readonly value: unknown;
    readonly previousValue: unknown;
    readonly sequence?: number;
    readonly alwaysWrite?: boolean;
}

export type PropertyOverResult =
    | { over: ProjectDocumentOver; sequence: number }
    | { reverted: ProjectDocumentOver }
    | { issue: string };

/** Serialize one captured edit; adapters supply ownership and live-target checks. */
export function writePropertyOver(input: PropertyOverInput, encodeNode?: (object: object) => unknown): PropertyOverResult {
    const value = captureDocumentValue(input.value, encodeNode);
    if (value === undefined) return { issue: "value type is not expressible in the document yet" };
    const base = captureDocumentValue(input.previousValue, encodeNode);
    if (!input.alwaysWrite && base !== undefined && JSON.stringify(base) === JSON.stringify(value)) {
        return { reverted: { target: input.target, value: base } };
    }
    return { over: { target: input.target, value }, sequence: input.sequence ?? 0 };
}

export function isDocumentReferenceValue(value: unknown): value is { ref: string; address?: NeedleAddress } {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Record<string, unknown>;
    if (typeof candidate.ref !== "string" || Object.keys(candidate).some(key => key !== "ref" && key !== "address")) return false;
    const address = candidate.address;
    return address === undefined || (!!address && typeof address === "object" && !Array.isArray(address)
        && Object.entries(address).every(([key, entry]) => ["id", "anchor", "type", "name", "path"].includes(key) && typeof entry === "string"));
}

export function captureDocumentValue(value: unknown, encodeNode?: (object: object) => unknown): unknown {
    if (value === null) return null;
    if (isDocumentReferenceValue(value)) return { ref: value.ref, ...(value.address ? { address: { ...value.address } } : {}) };
    switch (typeof value) {
        case "number": return Number.isFinite(value) ? value : undefined;
        case "string":
        case "boolean":
            return value;
        case "object": {
            // scene-object values (object slots) → NODE REFERENCES ({ref, address?});
            // unattributable objects fall through to undefined = "not expressible"
            if ((value as { isObject3D?: boolean }).isObject3D === true) {
                return encodeNode?.(value);
            }
            const toArray = (value as { toArray?: unknown }).toArray;
            if (typeof toArray === "function") {
                const array = (value as { toArray(): unknown[] }).toArray();
                if (Array.isArray(array)) {
                    // three value types are numeric arrays: Vector[234]/Quaternion/Color
                    // ([x,y,z(,w)]). Euler trails a string `order` — take the leading
                    // numeric run; the evaluator's Euler.fromArray([x,y,z]) keeps the
                    // node's own order. Reject anything with more than one trailing
                    // non-number (not a known value type).
                    const nums: number[] = [];
                    for (const v of array) { if (typeof v === "number" && Number.isFinite(v)) nums.push(v); else break; }
                    if (nums.length > 0 && (nums.length === array.length || (nums.length === array.length - 1 && typeof array[array.length - 1] === "string"))) return nums;
                }
            }
            if (Array.isArray(value) && value.every(v => (typeof v === "number" && Number.isFinite(v)) || typeof v === "string" || typeof v === "boolean")) {
                return [...value];
            }
            return undefined;
        }
        default:
            return undefined;
    }
}

export function documentOverKey(over: ProjectDocumentOver): string {
    const t = over.target;
    const addressKey = t.address?.id ?? t.address?.path ?? t.address?.name ?? "";
    return `${t.ref ?? ""}::${addressKey}::${t.path ?? ""}`;
}

/**
 * Merge base-document overs with freshly captured ones: a new over SUPERSEDES a base
 * over on the same target (the user re-edited it); base overs whose reference is gone
 * are dropped; everything else is preserved.
 */
export function mergeDocumentOvers(baseOvers: readonly ProjectDocumentOver[], newOvers: readonly ProjectDocumentOver[], liveRefs: ReadonlySet<string>, contentRefIds: ReadonlySet<string>, alwaysSuperseded?: ReadonlySet<string>, skipped?: string[]): ProjectDocumentOver[] {
    const superseded = new Set(newOvers.map(documentOverKey));
    const result: ProjectDocumentOver[] = [];
    for (const over of baseOvers) {
        // parent overs of the REGENERATED classes never merge back (re-derived
        // live every save): address-less = reference roots; addressed on a CONTENT
        // ref = content children. Addressed parent overs on OTHER refs are
        // ops-recorded asset moves — they PERSIST across sessions and supersede
        // by key on re-move.
        if (over.target.path === "parent" && (!over.target.address || (over.target.ref !== undefined && contentRefIds.has(over.target.ref)))) continue;
        if (over.target.ref && !liveRefs.has(over.target.ref)) {
            // reference removed — the over dies with it. EXPECTED after a reference
            // delete, but SURFACED regardless ("Save Project never drops an edit
            // silently"): an unexpected entry here is the symptom of an attribution
            // bug (wrong ref id), and silence cost a debugging session once
            // (plans/project-format-v0.md item 7b).
            skipped?.push(`over ${over.target.path ?? "?"} on reference "${over.target.ref}" — reference no longer exists, over dropped`);
            continue;
        }
        const key = documentOverKey(over);
        if (superseded.has(key)) continue;                               // re-edited below
        if (alwaysSuperseded?.has(key)) continue;                        // rebuilt-from-live paths (placement)
        result.push(over);
    }
    result.push(...newOvers);
    return result;
}
